import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Users, Briefcase, BarChart3, Building, Folder } from 'lucide-react';
import UnidadesNegocioConfig from './config/UnidadesNegocioConfig';
import AreasConfig from './config/AreasConfig';
import ServiciosConfig from './config/ServiciosConfig';
import PersonalConfig from './config/PersonalConfig';
import ReportesConfig from './config/ReportesConfig';
import ProyectosConfig from './config/ProyectosConfig';

type ConfigSection = 'overview' | 'unidadesnegocio' | 'areas' | 'servicios' | 'personal' | 'reportes' | 'proyectos';

const Configuration: React.FC = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<ConfigSection>(() => {
    // Obtener sección guardada del localStorage
    const savedSection = localStorage.getItem('consultoraHub_configSection');
    console.log('🔍 Estado recuperado del localStorage:', savedSection);
    
    // Validar que el valor guardado sea una sección válida
    const validSections: ConfigSection[] = ['overview', 'unidadesnegocio', 'areas', 'servicios', 'personal', 'reportes', 'proyectos'];
    if (savedSection && validSections.includes(savedSection as ConfigSection)) {
      return savedSection as ConfigSection;
    }
    
    console.log('⚠️ Valor inválido o no encontrado, usando overview por defecto');
    return 'overview';
  });

  // Persistir cambios de sección en localStorage
  useEffect(() => {
    console.log('💾 Guardando estado en localStorage:', activeSection);
    localStorage.setItem('consultoraHub_configSection', activeSection);
  }, [activeSection]);

  const renderContent = () => {
    switch (activeSection) {
      case 'unidadesnegocio':
        return <UnidadesNegocioConfig onBack={() => {
          console.log('📄 Volviendo de Unidades de Negocio a overview');
          setActiveSection('overview');
        }} />;
      case 'areas':
        return <AreasConfig onBack={() => {
          console.log('📄 Volviendo de Áreas a overview');
          setActiveSection('overview');
        }} />;
      case 'servicios':
        return <ServiciosConfig onBack={() => {
          console.log('📄 Volviendo de Servicios a overview');
          setActiveSection('overview');
        }} />;
      case 'personal':
        return <PersonalConfig onBack={() => {
          console.log('📄 Volviendo de Personal a overview');
          setActiveSection('overview');
        }} />;
      case 'reportes':
        return <ReportesConfig onBack={() => {
          console.log('📄 Volviendo de Reportes a overview');
          setActiveSection('overview');
        }} />;
      case 'proyectos':
        return <ProyectosConfig onBack={() => {
          console.log('📄 Volviendo de Proyectos a overview');
          setActiveSection('overview');
        }} />;
      default:
        return (
          <div className="space-y-6">
            <div className="text-center py-8">
              <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50 mb-2">
                Configuración del Sistema
              </h2>
              <p className="text-neutral-600 dark:text-neutral-400">
                Administra las áreas, servicios y personal de tu organización
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {/* Configuración de Unidades de Negocio */}
              <div
                onClick={() => {
                  console.log('🖱️ Haciendo clic en Unidades de Negocio');
                  setActiveSection('unidadesnegocio');
                }}
                className="card p-6 cursor-pointer transition-all hover:border-primary dark:hover:border-primary-dark"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 rounded-md flex items-center justify-center text-primary dark:text-primary-dark">
                    <Building className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">Unidades de Negocio</h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">Gestionar unidades</p>
                  </div>
                </div>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                  Administra las unidades de negocio de tu organización,
                  define su estructura y relaciones con las áreas.
                </p>
              </div>

              {/* Configuración de Áreas */}
              <div
                onClick={() => {
                  console.log('🖱️ Haciendo clic en Áreas');
                  setActiveSection('areas');
                }}
                className="card p-6 cursor-pointer transition-all hover:border-primary dark:hover:border-primary-dark"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 rounded-md flex items-center justify-center text-primary dark:text-primary-dark">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">Áreas</h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">Gestionar departamentos</p>
                  </div>
                </div>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                  Administra las diferentes áreas y departamentos de tu organización,
                  define sus responsabilidades y estructura.
                </p>
              </div>

              {/* Configuración de Servicios */}
              <div
                onClick={() => {
                  console.log('🖱️ Haciendo clic en Servicios');
                  setActiveSection('servicios');
                }}
                className="card p-6 cursor-pointer transition-all hover:border-primary dark:hover:border-primary-dark"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 rounded-md flex items-center justify-center text-primary dark:text-primary-dark">
                    <Briefcase className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">Servicios</h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">Gestionar proyectos</p>
                  </div>
                </div>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                  Configura los servicios y proyectos disponibles, asigna áreas
                  responsables y define características.
                </p>
              </div>

              {/* Configuración de Personal */}
              <div
                onClick={() => {
                  console.log('🖱️ Haciendo clic en Personal');
                  setActiveSection('personal');
                }}
                className="card p-6 cursor-pointer transition-all hover:border-primary dark:hover:border-primary-dark"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 rounded-md flex items-center justify-center text-primary dark:text-primary-dark">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">Personal</h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">Gestionar empleados</p>
                  </div>
                </div>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                  Administra la información del personal, asigna áreas de trabajo
                  y mantén actualizada la información de contacto.
                </p>
              </div>

              {/* Configuración de Reportes */}
              <div
                onClick={() => {
                  console.log('🖱️ Haciendo clic en Reportes');
                  setActiveSection('reportes');
                }}
                className="card p-6 cursor-pointer transition-all hover:border-primary dark:hover:border-primary-dark"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 rounded-md flex items-center justify-center text-primary dark:text-primary-dark">
                    <BarChart3 className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">Reportes</h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">Gestionar Power BI</p>
                  </div>
                </div>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                  Administra los reportes de Power BI disponibles, define permisos
                  por roles y organiza las categorías.
                </p>
              </div>

              {/* Configuración de Proyectos */}
              <div
                onClick={() => {
                  console.log('🖱️ Haciendo clic en Proyectos');
                  setActiveSection('proyectos');
                }}
                className="card p-6 cursor-pointer transition-all hover:border-primary dark:hover:border-primary-dark"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 rounded-md flex items-center justify-center text-primary dark:text-primary-dark">
                    <Folder className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">Proyectos</h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">Gestionar proyectos</p>
                  </div>
                </div>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                  Administra los proyectos por área, asigna servicios utilizados
                  y realiza seguimiento del estado y fechas.
                </p>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 transition-theme">
      {/* Header */}
      <div className="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  console.log('🔙 Volviendo al Dashboard, reseteando a overview');
                  // Resetear a overview cuando vuelve al dashboard
                  setActiveSection('overview');
                  navigate('/');
                }}
                className="flex items-center space-x-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-all"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Volver al Dashboard</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderContent()}
      </div>
    </div>
  );
};

export default Configuration;